
import { Button } from "@/components/ui/button";
import { ArrowLeft, Copy, ChartLine, Users, PercentCircle } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const CopyTrading = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F1F0FB] to-white">
      <Header />
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/knowledge-hub/basics">
            <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#9b87f5]/10 hover:text-[#9b87f5]">
              <ArrowLeft className="h-4 w-4" />
              Back to Crypto Basics
            </Button>
          </Link>
        </div>

        <article className="prose prose-slate lg:prose-lg max-w-4xl mx-auto bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-[#4A607B] text-center !mt-0">
            Copy Crypto Trading
          </h1>

          <p className="text-lg text-gray-700 mb-8 text-center max-w-3xl mx-auto">
            Copy trading is a trading strategy that enables investors to automatically replicate the trades of experienced or successful traders.
          </p>

          <div className="space-y-12">
            <section className="mb-12">
              <p className="text-gray-700">
                Instead of independently analyzing markets or executing trades, users select one or more traders whose strategies and performance they wish to follow. Whenever these traders buy or sell, the same trades are executed on the follower's account—often proportionally to the investment amount.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#4A607B] mb-6">Key Points</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Copy className="h-5 w-5 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#4A607B]">Automation</h3>
                  </div>
                  <p className="text-gray-700">
                    Trades are copied in real time. This hands-free approach means all decisions made by the chosen trader(s) are reflected immediately in the follower's account.
                  </p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-green-100">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#4A607B]">Experience and Accessibility</h3>
                  </div>
                  <p className="text-gray-700">
                    Copy trading can be especially attractive to beginners or those with limited time for market research, as they can leverage the knowledge of seasoned professionals.
                  </p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <ChartLine className="h-5 w-5 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#4A607B]">Diversification</h3>
                  </div>
                  <p className="text-gray-700">
                    Many platforms allow investors to follow multiple traders at once, helping to spread risk across different strategies.
                  </p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-orange-100">
                      <PercentCircle className="h-5 w-5 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#4A607B]">Risks</h3>
                  </div>
                  <p className="text-gray-700">
                    If the chosen trader makes a loss, followers also incur losses. Past performance is not a guarantee of future success, making it crucial to research traders carefully.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#4A607B] mb-4">Platforms</h2>
              <p className="text-gray-700">
                Copy trading is offered on specialized trading platforms and social trading networks. In the cryptocurrency space, many exchanges now integrate copy trading features, given the market's volatility and rapid trading opportunities.
              </p>
            </section>

            <section>
              <div className="bg-blue-50/80 backdrop-blur-sm p-6 rounded-xl">
                <h2 className="text-2xl font-semibold text-[#4A607B] mb-4">Overall Summary</h2>
                <p className="text-gray-700">
                  Copy trading provides a potentially simpler entry point for individuals who want exposure to various markets (including cryptocurrencies) but lack the expertise or time to trade on their own. It is essential, however, for investors to carefully assess the track records and trading styles of the experts they choose to follow, while remaining aware that no strategy is risk-free.
                </p>
              </div>
            </section>
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
};

export default CopyTrading;
